import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { getEnv } from '@config';
import { Icon } from '..';
import NavMenuItem from './NavMenuItem';
import { buildPaths } from '../../navigation';

import { isAllowed } from '../../helpers/auth';
import useAuth from '../../hooks/useAuth';

import usePromotions from '../../hooks/usePromotions';

const NavLinkStyled = styled(NavLink)`
  text-decoration: none;
  &.active > div {
    border-left: 8px solid #f8f8f8;
    padding-left: 16px;
    div {
      color: #f8f8f8;
    }
    svg {
      color: #f8f8f8;
    }
    .bullet {
      left: 7px;
    }
  }
`;

const Menu = styled.div`
  color: #c0dbf3;
`;

const Links = styled.div`
  transition: height 1s cubic-bezier(0.4, 0, 0.2, 1);
  height: ${({ isOpen }) => (isOpen ? 'auto' : '0px')};
  overflow: hidden;
`;

const Expand = styled.div`
  display: 'block';
  padding: 2px 0;
  text-align: right;
  flex: 1;
`;

const WithSubMenu = styled.div`
  width: 100%;
  display: flex;
  padding-right: 8px;
  .icon {
    transition: all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms;
    transform: rotate(0deg);
    &.up {
      transform: rotate(180deg);
    }
  }
`;

const NavMenuItemStyled = styled(NavMenuItem)`
  &.active {
    div {
      color: #f8f8f8;
    }
    svg {
      color: #f8f8f8;
    }
  }
`;

/**
 * Navigation Menu Component create link from routes list
 *
 * @component
 * @param {Object} params - Component params
 * @param {Object} params.routes - routes list configuration
 * @param {Object} [params.navRoute = null] - route submenu object
 * @param {Object} [params.navExpanded] - flag to show or not the link title
 * @param {Object} [params.routeSelected] - route selected
 * @example
 * const routes = [
 *  {
 *    name: "PARENT",
 *    path: "/parent",
 *    showOnNav: false,
 *    component: <div id="unique" />,
 *    routes: [{
 *        name: "PATHA",
 *        path: "/children-A",
 *        showOnNav: false,
 *        component: <div id="unique" />,
 *    },
 *    {
 *        name: "PATHB",
 *        path: "/children-B",
 *        showOnNav: false,
 *        component: <div id="unique" />,
 *    }],
 *  },
 * ];
 * return (
 *  <NavMenu routes={routes}/>
 * )
 */
const NavMenu = ({ routes = [], navRoute = null, navExpanded, routeSelected }) => {
  const { t } = useTranslation();
  const { isRL, isSeller } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const { promotionEnablesWidget } = usePromotions();
  const { avoidPromotionValidation } = getEnv();
  const showFinance =
    isRL() && isSeller() && (avoidPromotionValidation === 'true' || promotionEnablesWidget());

  return (
    <Menu>
      {navRoute && (
        <NavMenuItemStyled
          className={
            navRoute.routes.find((children) => children.name === routeSelected.name) && 'active'
          }
          id={`sidebar_${navRoute.name.toLowerCase()}`}
          onClick={() => setIsOpen(!isOpen)}
          icon={navRoute.icon}
          title={
            <WithSubMenu>
              <div>{t(navRoute.title)}</div>
              <Expand isOpen={navExpanded}>
                <Icon className={`icon ${isOpen ? 'up' : 'down'}`} size="16" icon="select-down" />
              </Expand>
            </WithSubMenu>
          }
          navExpanded={navExpanded}
        />
      )}
      <Links isOpen={navRoute ? isOpen : true}>
        {buildPaths(routes)
          .filter((route) => route.showOnNav)
          // .filter((route) => (!isRL() ? !(route.name === 'HOME') : true))
          .filter(
            (route) =>
              isAllowed(route.name) ||
              (route.routes && route.routes.filter((route) => isAllowed(route.name)).length > 0)
          )
          // TODO: Unificar filtro con el router
          .filter((route) => (route.name === 'FINANCE' ? showFinance : true))
          .map((route, index) =>
            route.routes &&
            route.routes
              .filter((route) => route.showOnNav)
              .filter(
                (route) =>
                  isAllowed(route.name) ||
                  (route.routes && route.routes.filter((route) => isAllowed(route.name)).length > 0)
              ).length > 0 ? (
              <NavMenu
                key={index}
                routes={route.routes}
                navRoute={route}
                navExpanded={navExpanded}
                routeSelected={routeSelected}
              />
            ) : (
              <NavLinkStyled
                key={index}
                to={route.path}
                activeClassName="active"
                id={route.htmlId || `sidebar_${route.name.toLowerCase()}`}
              >
                <NavMenuItem
                  icon={route.icon}
                  title={route.nameOnNav ? t(route.nameOnNav) : t(route.title)}
                  navExpanded={navExpanded}
                  routeSelected={routeSelected}
                  showBullet={route.showBullet}
                />
              </NavLinkStyled>
            )
          )}
      </Links>
    </Menu>
  );
};

NavMenu.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      showOnNav: PropTypes.bool.isRequired,
      component: PropTypes.any.isRequired,
    })
  ).isRequired,
};

export default NavMenu;
